import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { TextField, SortMenu, Button, ButtonMenu } from "@ann/web-components";
import {
  FiltersContainer,
  FiltersLeftContent,
  FiltersRightContent,
  SearchContainer,
} from "./Filters.styled";

const Filters = ({
  onSearchChange,
  onDepartmentChange,
  onOwnerChange,
  onModifiedChange,
  onSortChange,
  onOrderChange,
  selectedSortOption = "name",
  orderDirection = "asc",
  loading = false,
  knowledgeBases = [], // Receive knowledge bases to extract filter options
  departmentsList = [], // Receive departments list from API
  usersList = [], // Receive users list from API
  selectedDepartment: externalSelectedDepartment = null,
  selectedOwner: externalSelectedOwner = null,
  selectedModified: externalSelectedModified = null,
  searchKeyword = "",
}) => {
  const [searchValue, setSearchValue] = useState(searchKeyword);
  const [filtersKey, setFiltersKey] = useState(0);
  const [selectedDepartment, setSelectedDepartment] = useState(externalSelectedDepartment || []);
  const [selectedOwner, setSelectedOwner] = useState(externalSelectedOwner || []);
  const [selectedModified, setSelectedModified] = useState(externalSelectedModified || []);

  const searchTimeoutRef = useRef(null);

  // Use departments from API if available, otherwise extract from knowledge bases
  const departmentOptions = useMemo(() => {
    // If departmentsList is provided, use it
    if (departmentsList && departmentsList.length > 0) {
      return departmentsList
        .map((dept) => ({
          label: dept.name,
          value: dept.name.toLowerCase().replace(/\s+/g, "_"),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
    
    // Fallback: Extract unique departments/categories from knowledge bases
    const departments = new Set();
    knowledgeBases.forEach((kb) => {
      if (kb.category && kb.category !== "Unknown") {
        departments.add(kb.category);
      }
    });
    return Array.from(departments)
      .sort()
      .map((dept) => ({
        label: dept,
        value: dept.toLowerCase().replace(/\s+/g, "_"),
      }));
  }, [departmentsList, knowledgeBases]);

  // Use users from API if available, otherwise extract from knowledge bases
  const ownerOptions = useMemo(() => {
    // If usersList is provided, use it
    if (usersList && usersList.length > 0) {
      return usersList
        .map((user) => ({
          label: user.name,
          value: user.name.toLowerCase().replace(/\s+/g, "_"),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
    
    // Fallback: Extract unique owners from knowledge bases
    const owners = new Set();
    knowledgeBases.forEach((kb) => {
      if (kb.owner) {
        owners.add(kb.owner);
      }
    });
    return Array.from(owners)
      .sort()
      .map((owner) => ({
        label: owner,
        value: owner.toLowerCase().replace(/\s+/g, "_"),
      }));
  }, [usersList, knowledgeBases]);

  const modifiedOptions = [
    { label: "Last 7 days", value: "last_7_days" },
    { label: "Last 30 days", value: "last_30_days" },
    { label: "This year", value: "this_year" },
  ];

  const handleSearchChange = useCallback((value) => {
    setSearchValue(value);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout for 500ms debounce
    searchTimeoutRef.current = setTimeout(() => {
      onSearchChange?.(value);
    }, 500);
  }, [onSearchChange]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleDepartmentChange = (value) => {
    // ButtonMenu onChange receives the value of selected item
    // If same value is selected, clear the filter (toggle off)
    const newDepartment = selectedDepartment === value ? null : value;
    setSelectedDepartment(newDepartment ? [newDepartment] : []);
    if (onDepartmentChange) {
      onDepartmentChange(newDepartment ? [newDepartment] : null);
    }
  };

  const handleOwnerChange = (value) => {
    // ButtonMenu onChange receives the value of selected item
    // If same value is selected, clear the filter (toggle off)
    const newOwner = selectedOwner === value ? null : value;
    setSelectedOwner(newOwner ? [newOwner] : []);
    if (onOwnerChange) {
      onOwnerChange(newOwner ? [newOwner] : null);
    }
  };

  const handleModifiedChange = (value) => {
    // ButtonMenu onChange receives the value of selected item
    // If same value is selected, clear the filter (toggle off)
    const newModified = selectedModified === value ? null : value;
    setSelectedModified(newModified ? [newModified] : []);
    if (onModifiedChange) {
      onModifiedChange(newModified ? [newModified] : null);
    }
  };

  const handleClearAllFilters = useCallback(() => {
    setSelectedDepartment([]);
    setSelectedOwner([]);
    setSelectedModified([]);
    setSearchValue("");
    setFiltersKey((prev) => prev + 1);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    onSearchChange?.("");
    onDepartmentChange?.(null);
    onOwnerChange?.(null);
    onModifiedChange?.(null);
  }, [onSearchChange, onDepartmentChange, onOwnerChange, onModifiedChange]);

  // Get label for department filter button
  const getDepartmentLabel = () => {
    if (selectedDepartment && selectedDepartment.length > 0) {
      const selectedValue = selectedDepartment[0];
      const selectedOption = departmentOptions.find(opt => opt.value === selectedValue);
      return selectedOption ? selectedOption.label : "Department";
    }
    return "Department";
  };

  // Get label for owner filter button
  const getOwnerLabel = () => {
    if (selectedOwner && selectedOwner.length > 0) {
      const selectedValue = selectedOwner[0];
      const selectedOption = ownerOptions.find(opt => opt.value === selectedValue);
      return selectedOption ? selectedOption.label : "Owner";
    }
    return "Owner";
  };

  // Get label for modified filter button
  const getModifiedLabel = () => {
    if (selectedModified && selectedModified.length > 0) {
      const selectedValue = selectedModified[0];
      const selectedOption = modifiedOptions.find(opt => opt.value === selectedValue);
      return selectedOption ? selectedOption.label : "Modified";
    }
    return "Modified";
  };

  // Sync external state with internal state
  useEffect(() => {
    setSearchValue(searchKeyword);
  }, [searchKeyword]);

  useEffect(() => {
    setSelectedDepartment(externalSelectedDepartment || []);
  }, [externalSelectedDepartment]);

  useEffect(() => {
    setSelectedOwner(externalSelectedOwner || []);
  }, [externalSelectedOwner]);

  useEffect(() => {
    setSelectedModified(externalSelectedModified || []);
  }, [externalSelectedModified]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      (searchValue && searchValue.trim() !== "") ||
      (selectedDepartment && selectedDepartment.length > 0) ||
      (selectedOwner && selectedOwner.length > 0) ||
      (selectedModified && selectedModified.length > 0)
    );
  }, [searchValue, selectedDepartment, selectedOwner, selectedModified]);

  return (
    <FiltersContainer>
      <FiltersLeftContent>
        <SearchContainer>
          <TextField
            iconLeft="search"
            onChange={handleSearchChange}
            size="small"
            placeholder="Search"
            value={searchValue}
            disabled={loading}
          />
        </SearchContainer>
        <ButtonMenu
          key={`department-filter-${filtersKey}`}
          label={getDepartmentLabel()}
          menuPosition="bottom"
          onChange={handleDepartmentChange}
          size="medium"
          type="secondary"
          width="auto"
          disabled={loading}
          className="department-filter"
        >
          <ButtonMenu.Menu>
            {departmentOptions.map((option) => (
              <ButtonMenu.Item
                key={option.value}
                label={option.label}
                value={option.value}
              >
                {option.label}
              </ButtonMenu.Item>
            ))}
          </ButtonMenu.Menu>
        </ButtonMenu>
        <ButtonMenu
          key={`owner-filter-${filtersKey}`}
          label={getOwnerLabel()}
          menuPosition="bottom"
          onChange={handleOwnerChange}
          size="medium"
          type="secondary"
          width="auto"
          disabled={loading}
          className="owner-filter"
        >
          <ButtonMenu.Menu>
            {ownerOptions.map((option) => (
              <ButtonMenu.Item
                key={option.value}
                label={option.label}
                value={option.value}
              >
                {option.label}
              </ButtonMenu.Item>
            ))}
          </ButtonMenu.Menu>
        </ButtonMenu>
        <ButtonMenu
          key={`modified-filter-${filtersKey}`}
          label={getModifiedLabel()}
          menuPosition="bottom"
          onChange={handleModifiedChange}
          size="medium"
          type="secondary"
          width="auto"
          disabled={loading}
          className="modified-filter"
        >
          <ButtonMenu.Menu>
            {modifiedOptions.map((option) => (
              <ButtonMenu.Item
                key={option.value}
                label={option.label}
                value={option.value}
              >
                {option.label}
              </ButtonMenu.Item>
            ))}
          </ButtonMenu.Menu>
        </ButtonMenu>
        {hasActiveFilters && (
          <Button
            icon=""
            iconLeft=""
            iconRight=""
            label="Clear Filters"
            size="medium"
            type="secondary"
            onClick={handleClearAllFilters}
            disabled={loading}
          />
        )}
      </FiltersLeftContent>
      <FiltersRightContent>
        <SortMenu
          minWidth="200px"
          onChange={onSortChange || (() => {})}
          order={orderDirection.toLowerCase()}
          onOrderChange={onOrderChange || (() => {})}
          options={[
            {
              icon: 'sortByAlpha',
              id: 'name',
              label: 'Name (A-Z)'
            },
            {
              icon: 'clock',
              id: 'created',
              label: 'Created'
            },
            {
              icon: 'update',
              id: 'modified',
              label: 'Last Modified'
            }
          ]}
          selected={selectedSortOption}
          size="medium"
          title="SORT BY"
          width="240px"
          disabled={loading}
        />
      </FiltersRightContent>
    </FiltersContainer>
  );
};

export default Filters;

